import React, { useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';

import { useTranslation } from 'react-i18next';

import { Controller, useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from '@brand/components';
import {
  IAddressFieldSchema,
  IFieldProps,
  IFieldStyles,
} from 'schema/generator/FormSchema';

import { useErrorMessage } from 'hooks/form/useErrorMessage';
import { useStyles } from 'hooks/form/useStyles';

import ComponentIsVisible from 'components/utils/IsVisible';

const AddressField = ({
  field,
  id,
  name,
}: IFieldProps<IAddressFieldSchema>): JSX.Element => {
  const {
    label,
    placeholder,
    htmlInputType,
    isRequired,
    shouldDisplay,
    styles = {},
  } = field;

  const { t } = useTranslation('form');

  const fieldStyles = useStyles<IFieldStyles>('textField', styles);

  const { watch } = useFormContext();

  const errorMessage = useErrorMessage(name, label);

  const values = watch(name);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  return (
    <ComponentIsVisible when={isVisible}>
      <FormControl
        isInvalid={!!errorMessage}
        isRequired={isRequired}
        {...fieldStyles.control}
        key={`${name}-control`}
      >
        <>
          <Flex flexDirection={['column', null, 'row']} gap={6} width="100%">
            <Box width={['100%', null, '80%']}>
              <FormLabel
                htmlFor={field.inputs.labelStreet}
                marginBottom="4px"
                variant="proRikApp"
                {...fieldStyles.label}
              >
                {field.inputs.labelStreet}
              </FormLabel>
              <Controller
                defaultValue={field.inputs.valueStreet}
                name="headQuarters.street"
                render={({ field: controllerField }) => (
                  <Input
                    aria-label={field.inputs.labelStreet}
                    data-testid={id}
                    name={controllerField.name}
                    onChange={controllerField.onChange}
                    placeholder={placeholder}
                    sx={{
                      color: 'heading',
                      fontWeight: 'medium',
                      fontSize: '1rem',
                    }}
                    type={htmlInputType || 'text'}
                    value={controllerField.value}
                    {...fieldStyles.input}
                  />
                )}
              />

              <ComponentIsVisible when={!!errorMessage}>
                <FormErrorMessage
                  justifyContent="flex-end"
                  textAlign="right"
                  {...fieldStyles.errorMessage}
                >
                  {t(`validations.${errorMessage as string}`)}
                </FormErrorMessage>
              </ComponentIsVisible>
            </Box>
            <Box>
              <FormLabel
                htmlFor={field.inputs.labelNumber}
                marginBottom="4px"
                variant="proRikApp"
                {...fieldStyles.label}
              >
                {field.inputs.labelNumber}
              </FormLabel>
              <Controller
                defaultValue={field.inputs.valueNumber}
                name="headQuarters.number"
                render={({ field: controllerField }) => (
                  <Input
                    aria-label={field.inputs.labelNumber}
                    data-testid={id}
                    name={controllerField.name}
                    onChange={controllerField.onChange}
                    placeholder={placeholder}
                    sx={{
                      color: 'heading',
                      fontWeight: 'medium',
                      fontSize: '1rem',
                    }}
                    type={htmlInputType || 'number'}
                    value={controllerField.value}
                    {...fieldStyles.input}
                  />
                )}
              />
              <ComponentIsVisible when={!!errorMessage}>
                <FormErrorMessage
                  justifyContent="flex-end"
                  textAlign="right"
                  {...fieldStyles.errorMessage}
                >
                  {t(`validations.${errorMessage as string}`)}
                </FormErrorMessage>
              </ComponentIsVisible>
            </Box>
          </Flex>
          <Flex
            flexDirection={['column', null, 'row']}
            gap={6}
            height="fit-content"
            mt={4}
            width="100%"
          >
            <Box>
              <FormLabel
                htmlFor={field.inputs.labelZip}
                marginBottom="4px"
                variant="proRikApp"
                {...fieldStyles.label}
              >
                {field.inputs.labelZip}
              </FormLabel>
              <Controller
                defaultValue={field.inputs.valueZip}
                name="headQuarters.zip"
                render={({ field: controllerField }) => (
                  <Input
                    aria-label={field.inputs.labelZip}
                    data-testid={id}
                    name={controllerField.name}
                    onChange={controllerField.onChange}
                    placeholder={placeholder}
                    sx={{
                      color: 'heading',
                      fontWeight: 'medium',
                      fontSize: '1rem',
                    }}
                    type={htmlInputType || 'number'}
                    value={controllerField.value}
                    {...fieldStyles.input}
                  />
                )}
              />
              <ComponentIsVisible when={!!errorMessage}>
                <FormErrorMessage
                  justifyContent="flex-end"
                  textAlign="right"
                  {...fieldStyles.errorMessage}
                >
                  {t(`validations.${errorMessage as string}`)}
                </FormErrorMessage>
              </ComponentIsVisible>
            </Box>
            <Box width={['100%', null, '70%']}>
              <FormLabel
                htmlFor={field.inputs.labelCity}
                marginBottom="4px"
                variant="proRikApp"
                {...fieldStyles.label}
              >
                {field.inputs.labelCity}
              </FormLabel>
              <Controller
                defaultValue={field.inputs.valueCity}
                name="headQuarters.city"
                render={({ field: controllerField }) => (
                  <Input
                    aria-label={field.inputs.labelCity}
                    data-testid={id}
                    name={controllerField.name}
                    onChange={controllerField.onChange}
                    placeholder={placeholder}
                    sx={{
                      color: 'heading',
                      fontWeight: 'medium',
                      fontSize: '1rem',
                    }}
                    type={htmlInputType || 'text'}
                    value={controllerField.value}
                    {...fieldStyles.input}
                  />
                )}
              />
              <ComponentIsVisible when={!!errorMessage}>
                <FormErrorMessage
                  justifyContent="flex-end"
                  textAlign="right"
                  {...fieldStyles.errorMessage}
                >
                  {t(`validations.${errorMessage as string}`)}
                </FormErrorMessage>
              </ComponentIsVisible>
            </Box>
          </Flex>
        </>
      </FormControl>
    </ComponentIsVisible>
  );
};

export default AddressField;
